import type { FC } from 'react';
import { useMemo } from 'react';
import { Source, Layer } from 'react-map-gl';

import { usePoiCountryHierarchy } from '../../map-data/use-poi-hierarchy';
import { useMapData } from '../../map-data/use-map-data';

type Props = {
  hoveredCountryIso: string | null;
  selectedCountryIso: string | null;
};

export const PoiMapCountrySource: FC<Props> = ({
  hoveredCountryIso,
  selectedCountryIso,
}) => {
  const { countries } = usePoiCountryHierarchy();
  const { countryHeatMapMetaData } = useMapData();

  // Get ISO-3 codes for countries that have POI data matching current filters
  const countryIsoCodes = useMemo(() => {
    if (!countryHeatMapMetaData || !countries) return [];

    const isoCodes: string[] = [];
    for (const country of countries) {
      const countryData = Object.values(countryHeatMapMetaData).find(
        meta => meta.countryName === country.name,
      );
      if (countryData?.countryIso3166_1_3Name) {
        isoCodes.push(countryData.countryIso3166_1_3Name);
      }
    }
    return isoCodes;
  }, [countries, countryHeatMapMetaData]);

  const worldviewFilter = [
    'any',
    ['==', 'all', ['get', 'worldview']],
    ['in', 'US', ['get', 'worldview']],
  ] as const;

  if (countryIsoCodes.length === 0) {
    return null;
  }

  return (
    <Source type="vector" url="mapbox://mapbox.country-boundaries-v1">
      <Layer
        id="poi-country-fill"
        type="fill"
        source-layer="country_boundaries"
        beforeId="admin-0-boundary"
        filter={[
          'all',
          worldviewFilter,
          [
            'match',
            ['get', 'iso_3166_1_alpha_3'],
            countryIsoCodes,
            true,
            false,
          ],
        ]}
        paint={{
          'fill-opacity': 0,
        }}
      />

      {hoveredCountryIso && (
        <Layer
          id="poi-country-hover-outline"
          type="line"
          source-layer="country_boundaries"
          filter={[
            'all',
            worldviewFilter,
            ['==', ['get', 'iso_3166_1'], hoveredCountryIso],
          ]}
          paint={{
            'line-width': 1,
            'line-color': '#FFF',
          }}
        />
      )}

      {selectedCountryIso && (
        <Layer
          id="poi-country-select-outline"
          type="line"
          source-layer="country_boundaries"
          filter={[
            'all',
            worldviewFilter,
            ['==', ['get', 'iso_3166_1'], selectedCountryIso],
          ]}
          paint={{
            'line-width': 2,
            'line-color': '#FFF',
          }}
        />
      )}
    </Source>
  );
};
